import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { forgotPassword, verifyResetOtp, resetPassword, resendResetOtp } from '../api/api';

/* ── shared helpers ── */
const Spinner = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const EyeIcon = ({ show }) =>
  show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const Alert = ({ type, children }) => {
  const styles =
    type === 'error'
      ? { bg: '#fce0e8', border: '#f9b8ca', color: '#c44569' }
      : { bg: '#e8f5e9', border: '#a5d6a7', color: '#2e7d32' };
  return (
    <div className="mb-4 flex items-start gap-2 text-sm px-4 py-3 rounded-xl"
      style={{ background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color }}>
      <svg className="w-4 h-4 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
        {type === 'error'
          ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          : <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        }
      </svg>
      <span>{children}</span>
    </div>
  );
};

const BlobBg = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: '#cf6a87' }} />
    <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: '#f5cd79' }} />
  </div>
);

/* ════════════════════════════════════════════════
   STEP 1 — Enter Email
════════════════════════════════════════════════ */
const StepEmail = ({ onNext }) => {
  const [email, setEmail]   = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await forgotPassword({ email });
      onNext({ email, devOtp: res.data?.data?.dev_otp || null });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
          style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)' }}>
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Forgot Password?</h2>
        <p className="text-slate-500 text-sm mt-1">Enter your email and we'll send you a reset code.</p>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
          <input
            className="w-full px-4 py-3 rounded-xl border text-slate-900 text-sm outline-none transition"
            style={{ borderColor: '#f9b8ca', background: '#fdf0f4' }}
            onFocus={e => { e.target.style.borderColor = '#c44569'; e.target.style.boxShadow = '0 0 0 3px rgba(196,69,105,0.15)'; e.target.style.background = '#fff'; }}
            onBlur={e => { e.target.style.borderColor = '#f9b8ca'; e.target.style.boxShadow = 'none'; e.target.style.background = '#fdf0f4'; }}
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com" required autoFocus
          />
        </div>
        <button
          className="w-full py-3 text-white font-semibold rounded-xl text-sm transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: '#c44569' }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#cf6a87'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#c44569'; }}
          type="submit" disabled={loading}
        >
          {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Sending Code...</span> : 'Send Reset Code'}
        </button>
      </form>

      <p className="text-center text-sm text-slate-500 mt-6">
        <Link to="/login" className="font-semibold hover:underline" style={{ color: '#c44569' }}>← Back to Login</Link>
      </p>
    </>
  );
};

/* ════════════════════════════════════════════════
   STEP 2 — Enter OTP
════════════════════════════════════════════════ */
const StepOtp = ({ email, devOtp, onNext, onBack }) => {
  const [otp, setOtp]         = useState(['', '', '', '', '', '']);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef([]);

  /* countdown timer for resend */
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otp];
    text.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError('Please enter the complete 6-digit code.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await verifyResetOtp({ email, otp: code });
      onNext({ email, otp: code, resetToken: res.data.data.reset_token });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');
    setResending(true);
    try {
      await resendResetOtp({ email });
      setSuccess('A new reset code has been sent to your email.');
      setOtp(['', '', '', '', '', '']);
      setCountdown(60);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
          style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)' }}>
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Enter Reset Code</h2>
        <p className="text-slate-500 text-sm mt-1">
          We sent a 6-digit code to <strong className="text-slate-700">{email}</strong>
        </p>
        {devOtp && (
          <p className="mt-2 text-xs font-mono px-3 py-1.5 rounded-lg inline-block"
            style={{ background: '#fef9ec', border: '1px solid #f5cd79', color: '#92400e' }}>
            Dev mode – OTP: <strong>{devOtp}</strong>
          </p>
        )}
      </div>

      {error   && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex justify-center gap-2" onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => (inputRefs.current[i] = el)}
              type="text" inputMode="numeric" maxLength={1}
              value={digit}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              className="w-11 h-13 text-center font-bold text-lg rounded-xl border outline-none transition"
              style={{
                borderColor: digit ? '#c44569' : '#f9b8ca',
                background: digit ? '#fce0e8' : '#fdf0f4',
                color: '#c44569',
                height: '52px',
              }}
              onFocus={e => { e.target.style.borderColor = '#c44569'; e.target.style.boxShadow = '0 0 0 3px rgba(196,69,105,0.15)'; }}
              onBlur={e => { e.target.style.boxShadow = 'none'; }}
            />
          ))}
        </div>

        <button
          className="w-full py-3 text-white font-semibold rounded-xl text-sm transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: '#c44569' }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#cf6a87'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#c44569'; }}
          type="submit" disabled={loading}
        >
          {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Verifying...</span> : 'Verify Code'}
        </button>
      </form>

      <div className="mt-5 text-center">
        {countdown > 0 ? (
          <p className="text-sm text-slate-400">Resend code in <strong className="text-slate-600">{countdown}s</strong></p>
        ) : (
          <button
            onClick={handleResend} disabled={resending}
            className="text-sm font-semibold hover:underline disabled:opacity-50"
            style={{ color: '#c44569' }}
          >
            {resending ? 'Sending...' : 'Resend Code'}
          </button>
        )}
      </div>

      <p className="text-center text-sm text-slate-500 mt-3">
        <button onClick={onBack} className="font-semibold hover:underline" style={{ color: '#c44569' }}>
          ← Change Email
        </button>
      </p>
    </>
  );
};

/* ════════════════════════════════════════════════
   STEP 3 — Set New Password
════════════════════════════════════════════════ */
const StepNewPassword = ({ email, otp, resetToken }) => {
  const navigate = useNavigate();
  const [password, setPassword]       = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);

  const strength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthLabel = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'][strength];
  const strengthColor = ['', '#ef4444', '#f97316', '#eab308', '#22c55e', '#15803d'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await resetPassword({ email, otp, reset_token: resetToken, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
          style={{ background: '#e8f5e9', border: '2px solid #a5d6a7' }}>
          <svg className="w-8 h-8" fill="none" stroke="#2e7d32" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Password Reset!</h2>
        <p className="text-slate-500 text-sm mb-6">Your password has been updated. You can now log in with your new password.</p>
        <button
          onClick={() => navigate('/login')}
          className="w-full py-3 text-white font-semibold rounded-xl text-sm shadow-md"
          style={{ background: '#c44569' }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
          style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)' }}>
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Set New Password</h2>
        <p className="text-slate-500 text-sm mt-1">Choose a strong password for your account.</p>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* New Password */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
          <div className="relative">
            <input
              className="w-full px-4 py-3 pr-11 rounded-xl border text-slate-900 text-sm outline-none transition"
              style={{ borderColor: '#f9b8ca', background: '#fdf0f4' }}
              onFocus={e => { e.target.style.borderColor = '#c44569'; e.target.style.boxShadow = '0 0 0 3px rgba(196,69,105,0.15)'; e.target.style.background = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#f9b8ca'; e.target.style.boxShadow = 'none'; e.target.style.background = '#fdf0f4'; }}
              type={showPass ? 'text' : 'password'}
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Min. 6 characters" required autoFocus
            />
            <button type="button" tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowPass(s => !s)}>
              <EyeIcon show={showPass} />
            </button>
          </div>
          {/* Strength bar */}
          {password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <div key={n} className="flex-1 h-1 rounded-full transition-all"
                    style={{ background: n <= strength ? strengthColor : '#f1f5f9' }} />
                ))}
              </div>
              <p className="text-xs" style={{ color: strengthColor }}>{strengthLabel}</p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password</label>
          <div className="relative">
            <input
              className="w-full px-4 py-3 pr-11 rounded-xl border text-slate-900 text-sm outline-none transition"
              style={{ borderColor: '#f9b8ca', background: '#fdf0f4' }}
              onFocus={e => { e.target.style.borderColor = '#c44569'; e.target.style.boxShadow = '0 0 0 3px rgba(196,69,105,0.15)'; e.target.style.background = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#f9b8ca'; e.target.style.boxShadow = 'none'; e.target.style.background = '#fdf0f4'; }}
              type={showConfirm ? 'text' : 'password'}
              value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter new password" required
            />
            <button type="button" tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setShowConfirm(s => !s)}>
              <EyeIcon show={showConfirm} />
            </button>
          </div>
          {confirm && password !== confirm && (
            <p className="text-xs mt-1" style={{ color: '#ef4444' }}>Passwords do not match</p>
          )}
          {confirm && password === confirm && confirm.length >= 6 && (
            <p className="text-xs mt-1" style={{ color: '#22c55e' }}>Passwords match ✓</p>
          )}
        </div>

        <button
          className="w-full py-3 text-white font-semibold rounded-xl text-sm transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          style={{ background: '#c44569' }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#cf6a87'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#c44569'; }}
          type="submit" disabled={loading}
        >
          {loading ? <span className="flex items-center justify-center gap-2"><Spinner /> Saving...</span> : 'Reset Password'}
        </button>
      </form>
    </>
  );
};

/* ════════════════════════════════════════════════
   Main Page — orchestrates the 3 steps
════════════════════════════════════════════════ */
const STEPS = ['email', 'otp', 'password'];
const stepLabels = ['Email', 'Verify Code', 'New Password'];

const ForgotPasswordPage = () => {
  const [step, setStep]         = useState('email');
  const [email, setEmail]       = useState('');
  const [devOtp, setDevOtp]     = useState(null);
  const [otp, setOtp]           = useState('');
  const [resetToken, setResetToken] = useState('');

  const currentIndex = STEPS.indexOf(step);

  const handleEmailDone = ({ email: e, devOtp: d }) => {
    setEmail(e);
    setDevOtp(d);
    setStep('otp');
  };

  const handleOtpDone = ({ email: e, otp: o, resetToken: t }) => {
    setEmail(e);
    setOtp(o);
    setResetToken(t);
    setStep('password');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg,#fdf0f4 0%,#fff 50%,#fef9ec 100%)' }}>
      <BlobBg />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-lg mb-4" style={{ background: '#c44569' }}>
            <Wallet size={28} color="#fff" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">EMS</h1>
          <p className="text-slate-500 text-sm mt-1">Expense Management App</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center mb-6 gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    background: i < currentIndex ? '#22c55e' : i === currentIndex ? '#c44569' : '#f1f5f9',
                    color: i <= currentIndex ? '#fff' : '#94a3b8',
                  }}>
                  {i < currentIndex
                    ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    : i + 1}
                </div>
                <span className="text-xs mt-1 font-medium"
                  style={{ color: i === currentIndex ? '#c44569' : '#94a3b8' }}>
                  {stepLabels[i]}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="w-12 h-0.5 mb-4 mx-1"
                  style={{ background: i < currentIndex ? '#22c55e' : '#f1f5f9' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border p-8" style={{ borderColor: '#f9b8ca' }}>
          {step === 'email' && <StepEmail onNext={handleEmailDone} />}
          {step === 'otp'   && <StepOtp email={email} devOtp={devOtp} onNext={handleOtpDone} onBack={() => setStep('email')} />}
          {step === 'password' && <StepNewPassword email={email} otp={otp} resetToken={resetToken} />}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
